import React, { useState, useCallback } from 'react';
import { Trans, t } from '@lingui/macro';
import { Mic, Square, Keyboard, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { useSpeechToText } from '../../hooks/useSpeechToText';
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { analyzeMemo } from '../../services/AiMemoAnalyzer';
import { cn } from '../../lib/utils';
import VoiceWaveform from './VoiceWaveform';
import { KeepDeviceAwake } from '../../services/Utils/KeepDeviceAwake';
import { IconSparkles } from '../components/FigmaIcons';

interface AiMemoCreateProps {
    roomId: string;
    onSaved: (id: string) => void;
    onCancel: () => void;
}

const AiMemoCreate: React.FC<AiMemoCreateProps> = ({ roomId, onSaved, onCancel }) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [inputMode, setInputMode] = useState<'voice' | 'text'>('text');
    const [isSaving, setIsSaving] = useState(false);
    const [hasRecorded, setHasRecorded] = useState(false);

    // Detect language from user settings
    const sttLang = My.user?.lang === 'ja' ? 'ja-JP' : My.user?.lang === 'en' ? 'en-US' : 'ko-KR';

    const {
        isListening,
        isSupported,
        transcript,
        interimTranscript,
        error: sttError,
        isConverting,
        startListening,
        stopListening,
        resetTranscript,
        audioStream,
    } = useSpeechToText({ lang: sttLang });

    const handleToggleVoice = useCallback(() => {
        if (isListening) {
            stopListening();
            KeepDeviceAwake.allowSleep();
        } else {
            setHasRecorded(true);
            startListening();
            KeepDeviceAwake.keepAwake();
        }
    }, [isListening, startListening, stopListening]);

    const handleSave = async () => {
        // If still in voice mode, stop and append
        if (isListening) {
            stopListening();
            KeepDeviceAwake.allowSleep();
        }

        // 음성 모드에서는 transcript가 곧 콘텐츠
        const finalContent = inputMode === 'voice' ? transcript : content;

        if (!finalContent.trim()) return;

        setIsSaving(true);
        try {
            // personal room = uid
            // roomId from props
            const userId = My.user?.id;
            if (!roomId || !userId) throw new Error('User not logged in');

            const memoId = await Fire.aiMemo.saveMemo(roomId, userId, {
                title: title.trim() || '',
                content: finalContent,
                paragraphs: [{
                    id: Date.now().toString(),
                    content: finalContent,
                    source: inputMode,
                    created: new Date(),
                }],
                category: '',
                tags: [],
                language: sttLang,
                status: 'saved',
            });

            // AI 분석 (클라이언트 사이드, 백그라운드)
            analyzeMemo(finalContent, title.trim(), sttLang)
                .then(async (result) => {
                    await Fire.aiMemo.updateMemo(roomId, memoId, {
                        category: result.category,
                        tags: result.tags,
                        summary: result.summary,
                        ...(result.suggestedTitle ? { title: result.suggestedTitle } : {}),
                    } as any);
                })
                .catch(err => console.warn('[AI Memo] Analysis failed:', err));

            onSaved(memoId);
        } catch (e) {
            console.error('Failed to save memo:', e);
        } finally {
            setIsSaving(false);
        }
    };

    const displayContent = inputMode === 'voice'
        ? content + (transcript ? (content ? '\n' : '') + transcript : '')
        : content;

    return (
        <div className="p-5 flex flex-col h-full">

            {/* Input Mode Toggle */}
            <div className="flex gap-2 mb-5 shrink-0">
                <Button
                    variant={inputMode === 'text' ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => {
                        if (isListening) stopListening();
                        setInputMode('text');
                    }}
                >
                    <Keyboard className="w-4 h-4 mr-2" />
                    <Trans>Text</Trans>
                </Button>
                <Button
                    variant={inputMode === 'voice' ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setInputMode('voice')}
                    disabled={!isSupported}
                >
                    <Mic className="w-4 h-4 mr-2" />
                    <Trans>Voice</Trans>
                </Button>
            </div>

            {/* Content Area — flex-1로 남은 공간 채움, 스크롤은 내부 요소에만 */}
            <div className="flex-1 flex flex-col min-h-0 mb-5">
                {/* Voice Input Area */}
                {inputMode === 'voice' && (
                    <div className="flex flex-col flex-1 min-h-0 space-y-2">
                        {/* Mic Button */}
                        <div className="flex flex-col items-center gap-2 py-3 shrink-0">
                            <button
                                onClick={handleToggleVoice}
                                className={cn(
                                    "w-20 h-20 rounded-full flex items-center justify-center transition-all shadow-lg border",
                                    isListening
                                        ? "border-destructive bg-destructive hover:bg-destructive/90 animate-pulse shadow-destructive/50"
                                        : "bg-background border-border hover:bg-card"
                                )}
                            >
                                {isListening ? (
                                    <Square className="w-8 h-8 text-on-primary-text" />
                                ) : (
                                    <Mic className="w-8 h-8 text-foreground" />
                                )}
                            </button>
                            <p className="text-xs text-muted-foreground">
                                {isListening
                                    ? t`Recording... Tap to stop`
                                    : t`Tap to start voice recording`}
                            </p>
                            <VoiceWaveform isActive={isListening} audioStream={audioStream} />
                        </div>

                        {/* Live Transcript — 남은 공간을 채우며 내부 스크롤 */}
                        {(hasRecorded || transcript || isConverting) && (
                            <div className="relative p-3 bg-muted/50 rounded-xl border min-h-[60px] flex-1 overflow-y-auto">
                                <p className="text-sm whitespace-pre-wrap">
                                    {transcript}
                                    {interimTranscript && (
                                        <span className="text-muted-foreground">{interimTranscript}</span>
                                    )}
                                </p>
                                {isConverting && (
                                    <div className="flex items-center gap-2 mt-2 text-xs text-purple-500">
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        {t`Converting speech to text...`}
                                    </div>
                                )}
                            </div>
                        )}

                        {sttError && (
                            <p className="text-sm text-destructive shrink-0">{sttError}</p>
                        )}
                    </div>
                )}

                {/* Text Content */}
                {inputMode === 'voice' && content && (
                    <div className="p-4 bg-ai-bg rounded-xl border border-ai/20 max-h-[30vh] overflow-y-auto shrink-0">
                        <p className="text-sm whitespace-pre-wrap text-foreground">{content}</p>
                    </div>
                )}
                {inputMode === 'text' && (
                    <Textarea
                        placeholder={t`Enter memo content...`}
                        value={content}
                        onChange={e => setContent(e.target.value)}
                        className="resize-none flex-1 min-h-0 overflow-y-auto"
                    />
                )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t shrink-0">
                <Button variant="outline" onClick={onCancel}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={isSaving || (!content.trim() && !transcript.trim())}
                    className=""
                >
                    {isSaving ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            <Trans>AI analyzing...</Trans>
                        </>
                    ) : (
                        <>
                            <IconSparkles className="w-4 h-4 mr-2" />
                            <Trans>Save & AI Analysis</Trans>
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
};

export default AiMemoCreate;
